/**
 * CompanySetupWizard.tsx
 *
 * A full-screen, multi-step first-run wizard shown to super_admin when
 * `company.setup_complete === false`.
 *
 * Step flow:
 *  1. Welcome            — intro screen, confirm company name
 *  2. Company Profile    — fill in name, industry, timezone, currency, logo
 *  3. Select Modules     — toggle which of the 8 modules to enable
 *  4. Departments        — create at least one department
 *  5. Done               — summary + mark setup_complete = true
 *
 * On completion it calls `updateCompany({ setup_complete: true })` which
 * updates the DB row and triggers a re-render in the parent (which no longer
 * renders the wizard once setup_complete is true).
 *
 * This component is rendered by App.tsx as an overlay on top of the normal
 * layout when the condition is met — it does NOT live in the router.
 */

import React, { useState, useCallback } from 'react';
import {
  Building2,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  LayoutDashboard,
  Users,
  Briefcase,
  HeadphonesIcon,
  MessageSquare,
  BarChart3,
  DollarSign,
  UserPlus,
  Zap,
  Globe,
  Clock,
  Coins,
  Loader2,
  Plus,
  Trash2,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useModules, ALL_MODULE_KEYS } from '../../context/ModuleContext';
import { useWorkspaceId } from '../../context/AuthContext';
import { ModuleKey } from '../../types';

// ─── Module metadata ──────────────────────────────────────────────────────────

interface ModuleMeta {
  key: ModuleKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  recommended: boolean;
}

const MODULE_META: ModuleMeta[] = [
  {
    key: 'hr',
    label: 'HR & Leave',
    description: 'Leave requests, attendance, documents, compliance.',
    icon: <Users size={20} />,
    color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    recommended: true,
  },
  {
    key: 'performance',
    label: 'Performance',
    description: 'KPIs, OKRs, weekly check-ins, performance reviews.',
    icon: <LayoutDashboard size={20} />,
    color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    recommended: true,
  },
  {
    key: 'projects',
    label: 'Projects & Tasks',
    description: 'Task boards, project tracking, time logging.',
    icon: <Briefcase size={20} />,
    color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    recommended: true,
  },
  {
    key: 'support',
    label: 'Support Desk',
    description: 'Ticketing system, SLA policies, knowledge base.',
    icon: <HeadphonesIcon size={20} />,
    color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    recommended: false,
  },
  {
    key: 'communication',
    label: 'Communication',
    description: 'Team channels and direct messaging.',
    icon: <MessageSquare size={20} />,
    color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    recommended: true,
  },
  {
    key: 'finance',
    label: 'Finance',
    description: 'Budgets, expenses, invoices, financial reports.',
    icon: <DollarSign size={20} />,
    color: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    recommended: false,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Cross-module reports and data visualisations.',
    icon: <BarChart3 size={20} />,
    color: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
    recommended: true,
  },
  {
    key: 'recruitment',
    label: 'Recruitment',
    description: 'Job postings, applicant pipeline, offer letters.',
    icon: <UserPlus size={20} />,
    color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
    recommended: false,
  },
];

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS = ['Welcome', 'Company Profile', 'Modules', 'Departments', 'Done'];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((label, idx) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center gap-1">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                idx < current
                  ? 'bg-green-500 text-white'
                  : idx === current
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/15 text-white/50 dark:bg-white/20 dark:text-white/40'
              }`}
            >
              {idx < current ? <CheckCircle2 size={16} /> : idx + 1}
            </div>
            <span className="text-xs text-white/50 dark:text-white/40 hidden sm:block">{label}</span>
          </div>
          {idx < STEPS.length - 1 && (
            <div
              className={`h-0.5 w-8 sm:w-16 transition-colors ${
                idx < current ? 'bg-green-500' : 'bg-white/15 dark:bg-white/20'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Department row editor ────────────────────────────────────────────────────

interface DeptDraft {
  id: string;
  name: string;
  color: string;
}

const DEPT_COLORS = [
  '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B',
  '#EF4444', '#EC4899', '#14B8A6', '#6366F1',
];

function DepartmentRow({
  dept,
  onChange,
  onRemove,
  canRemove,
}: {
  dept: DeptDraft;
  onChange: (d: DeptDraft) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  return (
    <div className="flex items-center gap-3 p-3 border border-white/10 dark:border-gray-700 rounded-lg">
      {/* Color swatch picker */}
      <div className="flex gap-1 flex-wrap w-36 shrink-0">
        {DEPT_COLORS.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => onChange({ ...dept, color: c })}
            className={`w-5 h-5 rounded-full border-2 transition-transform ${
              dept.color === c ? 'border-gray-800 dark:border-white scale-125' : 'border-transparent'
            }`}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <input
        type="text"
        value={dept.name}
        onChange={e => onChange({ ...dept, name: e.target.value })}
        placeholder="Department name"
        className="flex-1 px-3 py-2 text-sm border border-white/15 dark:border-gray-600 rounded-lg bg-black/20 dark:bg-white/20 text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-white/40 hover:text-red-500 dark:hover:text-red-400 transition-colors"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
}

// ─── Main wizard component ─────────────────────────────────────────────────────

interface CompanySetupWizardProps {
  /** Called after setup_complete is saved so the parent can unmount the wizard. */
  onComplete: () => void;
}

export default function CompanySetupWizard({ onComplete }: CompanySetupWizardProps) {
  const { company, updateCompany, toggleModule, moduleMap } = useModules();
  const { workspaceId } = useWorkspaceId();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Step 2 — Company Profile fields ──────────────────────────────────────
  const [companyName, setCompanyName]     = useState(company?.name ?? '');
  const [industry, setIndustry]           = useState(company?.industry ?? '');
  const [timezone, setTimezone]           = useState(company?.timezone ?? 'UTC');
  const [currency, setCurrency]           = useState(company?.currency ?? 'EUR');
  const [logoUrl, setLogoUrl]             = useState(company?.logo_url ?? '');
  const [website, setWebsite]             = useState(company?.website ?? '');

  // ── Step 3 — Module toggles (local copy of moduleMap) ────────────────────
  const [modules, setModules] = useState<Record<ModuleKey, boolean>>(
    () => ({ ...moduleMap })
  );

  // ── Step 4 — Departments ─────────────────────────────────────────────────
  const [depts, setDepts] = useState<DeptDraft[]>([
    { id: crypto.randomUUID(), name: '', color: DEPT_COLORS[0] },
  ]);

  // ─────────────────────────────────────────────────────────────────────────

  const next = () => {
    setError(null);
    setStep(s => s + 1);
  };

  const back = () => {
    setError(null);
    setStep(s => s - 1);
  };

  /** Validate and advance from step 2 (profile). */
  const saveProfile = useCallback(async () => {
    if (!companyName.trim()) { setError('Company name is required.'); return; }
    setSaving(true);
    const ok = await updateCompany({
      name:     companyName.trim(),
      industry: industry.trim() || undefined,
      timezone: timezone || 'UTC',
      currency: currency || 'EUR',
      logo_url: logoUrl.trim() || undefined,
      website:  website.trim() || undefined,
    });
    setSaving(false);
    if (!ok) { setError('Failed to save. Please try again.'); return; }
    next();
  }, [companyName, industry, timezone, currency, logoUrl, website, updateCompany]);

  /** Persist module toggles then advance. */
  const saveModules = useCallback(async () => {
    setSaving(true);
    const tasks = ALL_MODULE_KEYS.map(key => toggleModule(key, modules[key]));
    const results = await Promise.all(tasks);
    setSaving(false);
    if (results.some(r => !r)) { setError('Some modules failed to save. Please try again.'); return; }
    next();
  }, [modules, toggleModule]);

  /** Create department rows in Supabase then advance. */
  const saveDepartments = useCallback(async () => {
    const valid = depts.filter(d => d.name.trim());
    if (valid.length === 0) { setError('Add at least one department.'); return; }

    if (!workspaceId) { setError('Still signing you in — try again in a moment.'); return; }

    setSaving(true);
    const rows = valid.map(d => ({
      name:       d.name.trim(),
      color:      d.color,
      workspace_id: workspaceId,
    }));
    const { error: dbErr } = await supabase.from('departments').insert(rows);
    setSaving(false);
    if (dbErr) { setError(`Database error: ${dbErr.message}`); return; }
    next();
  }, [depts, workspaceId]);

  /** Mark setup_complete = true and call onComplete(). */
  const finish = useCallback(async () => {
    setSaving(true);
    const ok = await updateCompany({ setup_complete: true });
    setSaving(false);
    if (!ok) { setError('Failed to complete setup. Please try again.'); return; }
    onComplete();
  }, [updateCompany, onComplete]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-black/20 dark:bg-black/30 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-8 pt-8 pb-4 border-b border-white/10 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <Zap size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white dark:text-white">Welcome to CBOps</h1>
              <p className="text-sm text-white/50 dark:text-white/40">Let's get your workspace set up</p>
            </div>
          </div>
          <StepIndicator current={step} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">

          {/* ── Step 0: Welcome ── */}
          {step === 0 && (
            <div className="text-center py-4">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Building2 size={36} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white dark:text-white mb-3">
                Set up your workspace
              </h2>
              <p className="text-white/50 dark:text-white/40 max-w-md mx-auto mb-6">
                This quick wizard will walk you through configuring CBOps for your organisation.
                It only takes a couple of minutes.
              </p>
              <ul className="text-left space-y-2 max-w-xs mx-auto mb-8">
                {[
                  'Configure company profile',
                  'Choose which modules to enable',
                  'Create your first departments',
                ].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-white/70 dark:text-white/30">
                    <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Step 1: Company Profile ── */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white dark:text-white mb-4">Company Profile</h2>

              <div>
                <label className="block text-sm font-medium text-white/80 dark:text-white/30 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="Acme Inc."
                  className="w-full px-3 py-2 border border-white/15 dark:border-gray-600 rounded-lg bg-black/20 dark:bg-white/20 text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 dark:text-white/30 mb-1">Industry</label>
                  <select
                    value={industry}
                    onChange={e => setIndustry(e.target.value)}
                    className="w-full px-3 py-2 border border-white/15 dark:border-gray-600 rounded-lg bg-black/20 dark:bg-white/20 text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select…</option>
                    {['Technology', 'Finance', 'Healthcare', 'Retail', 'Manufacturing',
                      'Education', 'Legal', 'Real Estate', 'Consulting', 'Other'].map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 dark:text-white/30 mb-1">
                    <Globe size={14} className="inline mr-1" />Website
                  </label>
                  <input
                    type="url"
                    value={website}
                    onChange={e => setWebsite(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-white/15 dark:border-gray-600 rounded-lg bg-black/20 dark:bg-white/20 text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 dark:text-white/30 mb-1">
                    <Clock size={14} className="inline mr-1" />Timezone
                  </label>
                  <select
                    value={timezone}
                    onChange={e => setTimezone(e.target.value)}
                    className="w-full px-3 py-2 border border-white/15 dark:border-gray-600 rounded-lg bg-black/20 dark:bg-white/20 text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[
                      'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
                      'America/Los_Angeles', 'Europe/London', 'Europe/Paris',
                      'Europe/Berlin', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore',
                      'Asia/Tokyo', 'Australia/Sydney',
                    ].map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 dark:text-white/30 mb-1">
                    <Coins size={14} className="inline mr-1" />Currency
                  </label>
                  <select
                    value={currency}
                    onChange={e => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 border border-white/15 dark:border-gray-600 rounded-lg bg-black/20 dark:bg-white/20 text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'SGD', 'AED', 'JPY'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 dark:text-white/30 mb-1">Logo URL</label>
                <input
                  type="url"
                  value={logoUrl}
                  onChange={e => setLogoUrl(e.target.value)}
                  placeholder="https://cdn.example.com/logo.png"
                  className="w-full px-3 py-2 border border-white/15 dark:border-gray-600 rounded-lg bg-black/20 dark:bg-white/20 text-white dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* ── Step 2: Module Selection ── */}
          {step === 2 && (
            <div>
              <h2 className="text-lg font-semibold text-white dark:text-white mb-1">Choose Your Modules</h2>
              <p className="text-sm text-white/50 dark:text-white/40 mb-4">
                Enable the features your team needs. You can change this any time in Company Settings.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MODULE_META.map(mod => {
                  const enabled = modules[mod.key];
                  return (
                    <button
                      key={mod.key}
                      type="button"
                      onClick={() => setModules(prev => ({ ...prev, [mod.key]: !prev[mod.key] }))}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        enabled
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-white/10 dark:border-gray-700 hover:border-white/15 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${mod.color}`}>
                            {mod.icon}
                          </span>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-sm text-white dark:text-white">{mod.label}</span>
                              {mod.recommended && (
                                <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-white/50 dark:text-white/40 mt-0.5">{mod.description}</p>
                          </div>
                        </div>
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                            enabled
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-white/15 dark:border-gray-600'
                          }`}
                        >
                          {enabled && <CheckCircle2 size={12} className="text-white" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 3: Departments ── */}
          {step === 3 && (
            <div>
              <h2 className="text-lg font-semibold text-white dark:text-white mb-1">Create Departments</h2>
              <p className="text-sm text-white/50 dark:text-white/40 mb-4">
                Add at least one department. You can add more and assign team members later.
              </p>
              <div className="space-y-3">
                {depts.map((dept, idx) => (
                  <DepartmentRow
                    key={dept.id}
                    dept={dept}
                    onChange={updated => setDepts(prev => prev.map((d, i) => i === idx ? updated : d))}
                    onRemove={() => setDepts(prev => prev.filter((_, i) => i !== idx))}
                    canRemove={depts.length > 1}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => setDepts(prev => [...prev, {
                  id: crypto.randomUUID(),
                  name: '',
                  color: DEPT_COLORS[prev.length % DEPT_COLORS.length],
                }])}
                className="mt-3 flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
              >
                <Plus size={16} />
                Add another department
              </button>
            </div>
          )}

          {/* ── Step 4: Done ── */}
          {step === 4 && (
            <div className="text-center py-4">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-lg">
                <Sparkles size={36} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white dark:text-white mb-3">You're all set!</h2>
              <p className="text-white/50 dark:text-white/40 max-w-md mx-auto mb-6">
                CBOps is configured and ready to use. Your team can now log in and get started.
              </p>
              <div className="bg-white/5 dark:bg-white/20/50 rounded-xl p-4 text-left space-y-2 max-w-sm mx-auto">
                <p className="text-sm font-medium text-white/80 dark:text-white/30">Summary</p>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 dark:text-white/40">Company</span>
                  <span className="font-medium text-white dark:text-white/20">{companyName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 dark:text-white/40">Modules enabled</span>
                  <span className="font-medium text-white dark:text-white/20">
                    {ALL_MODULE_KEYS.filter(k => modules[k]).length} / {ALL_MODULE_KEYS.length}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 dark:text-white/40">Departments</span>
                  <span className="font-medium text-white dark:text-white/20">
                    {depts.filter(d => d.name.trim()).length}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <p className="mt-4 text-sm text-red-500 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-white/10 dark:border-gray-700 flex justify-between items-center shrink-0">
          <button
            type="button"
            onClick={back}
            disabled={step === 0 || saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white/70 dark:text-white/30 hover:text-white dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
            Back
          </button>

          {/* Primary CTA per step */}
          {step === 0 && (
            <button
              type="button"
              onClick={next}
              className="flex items-center gap-1.5 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Get started
              <ChevronRight size={16} />
            </button>
          )}

          {step === 1 && (
            <button
              type="button"
              onClick={saveProfile}
              disabled={saving}
              className="flex items-center gap-1.5 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
              Save & Continue
            </button>
          )}

          {step === 2 && (
            <button
              type="button"
              onClick={saveModules}
              disabled={saving}
              className="flex items-center gap-1.5 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
              Save & Continue
            </button>
          )}

          {step === 3 && (
            <button
              type="button"
              onClick={saveDepartments}
              disabled={saving}
              className="flex items-center gap-1.5 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
              Save & Continue
            </button>
          )}

          {step === 4 && (
            <button
              type="button"
              onClick={finish}
              disabled={saving}
              className="flex items-center gap-1.5 px-6 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              Launch CBOps
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
